import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X, Users, AlertCircle, Upload, Download } from 'lucide-react';
import type { BatchSubject, CheckType } from '@/types';
import { InvestigationCategory } from '@/types';
import { splitFullName, joinFullName } from '@/utils/nameParts';
import { parseSubjectFile, SUBJECT_CSV_TEMPLATE, SUBJECT_IMPORT_ACCEPT } from '@/utils/subjectImport';

interface BulkSubjectsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    onNext: (subjects: BatchSubject[]) => void;
    checkType: CheckType;
    planName: string;
    /** Charge for one person, so the running total can be shown as they add people. */
    pricePerSubject: number;
    externalLoading?: boolean;
    externalError?: string;
    /** Prefilled people, used when resuming a draft parked before signing in. */
    initialSubjects?: BatchSubject[];
}

/**
 * A row as edited on screen. People think in whole names and spreadsheets
 * store whole names, so that is what the form holds; it is split into the
 * API's first/middle/last only on submit.
 */
interface SubjectRow {
    fullName: string;
    employeeEmail: string;
    employeePhone: string;
    employeeGender: string;
    investigationCategory: string;
}

/** A blank person. Kept as a factory so each row gets its own object. */
const emptyRow = (): SubjectRow => ({
    fullName: '',
    employeeEmail: '',
    employeePhone: '',
    employeeGender: '',
    investigationCategory: '',
});

/** Turn a stored subject back into an editable row, for a resumed draft. */
function fromSubject(subject: BatchSubject): SubjectRow {
    return {
        fullName: joinFullName(subject),
        employeeEmail: subject.employeeEmail ?? '',
        employeePhone: subject.employeePhone ?? '',
        employeeGender: subject.employeeGender ?? '',
        investigationCategory: subject.investigationCategory ?? '',
    };
}

/** Turn an edited row into the payload the API takes. */
function toSubject(row: SubjectRow): BatchSubject | null {
    const parts = splitFullName(row.fullName);
    if (!parts) return null;
    return {
        employeeFirstName: parts.first,
        ...(parts.middle ? { employeeMiddleName: parts.middle } : {}),
        employeeLastName: parts.last,
        employeeEmail: row.employeeEmail.trim(),
        employeePhone: row.employeePhone,
        ...(row.employeeGender ? { employeeGender: row.employeeGender } : {}),
        ...(row.investigationCategory
            ? { investigationCategory: row.investigationCategory as InvestigationCategory }
            : {}),
    };
}

const MAX_SUBJECTS = 20;

function formatNaira(value: number): string {
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
    }).format(value || 0);
}

/**
 * Enter several people in one pass, for a single payment.
 *
 * Deliberately one screen rather than a wizard per person: the whole point of
 * the bulk flow is that an employer hiring four people fills one form and pays
 * once. Documents are not collected here — they are uploaded per person
 * afterwards, exactly as in the single flow, so a missing file never blocks the
 * payment for everyone else.
 */
export default function BulkSubjectsModal({
    isOpen,
    onClose,
    onBack,
    onNext,
    planName,
    pricePerSubject,
    externalLoading = false,
    externalError = '',
    initialSubjects,
}: BulkSubjectsModalProps) {
    const [subjects, setSubjects] = useState<SubjectRow[]>(
        initialSubjects?.length ? initialSubjects.map(fromSubject) : [emptyRow(), emptyRow()],
    );
    // Result of the last spreadsheet import, shown until they import again.
    const [importErrors, setImportErrors] = useState<string[]>([]);
    const [importNote, setImportNote] = useState('');
    const [touched, setTouched] = useState(false);

    // The draft is read asynchronously by the flow, so it can land after this
    // modal has already mounted with empty rows.
    useEffect(() => {
        if (initialSubjects?.length) setSubjects(initialSubjects.map(fromSubject));
    }, [initialSubjects]);

    const update = (index: number, patch: Partial<SubjectRow>) =>
        setSubjects((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));

    const addRow = () => setSubjects((prev) => [...prev, emptyRow()]);

    /**
     * Load people from a spreadsheet export.
     *
     * Replaces the rows rather than appending, because the common case is
     * "here is my list" rather than "add these to the two blanks I already
     * have". Rows that cannot be used are reported by their real row number
     * and the rest still import, so one typo does not reject the file.
     */
    const handleImport = async (file: File) => {
        setImportErrors([]);
        setImportNote('');

        const { subjects: imported, errors } = await parseSubjectFile(file);

        if (imported.length > 0) {
            setSubjects(
                imported.slice(0, MAX_SUBJECTS).map((row) => ({
                    fullName: row.fullName,
                    employeeEmail: row.employeeEmail,
                    employeePhone: row.employeePhone,
                    employeeGender: row.employeeGender ?? '',
                    investigationCategory: row.investigationCategory ?? '',
                })),
            );
            const capped = imported.length > MAX_SUBJECTS;
            setImportNote(
                `Imported ${Math.min(imported.length, MAX_SUBJECTS)} ${
                    imported.length === 1 ? 'person' : 'people'
                }.${capped ? ` Only the first ${MAX_SUBJECTS} were taken — that is the most one payment covers.` : ''}`,
            );
            setTouched(true);
        }

        setImportErrors(errors);
    };

    const downloadTemplate = () => {
        const blob = new Blob([SUBJECT_CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'yourchecka-people-template.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const removeRow = (index: number) =>
        setSubjects((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));

    // Validated here as well as on the server, so an employer finds a typo
    // before a payment rather than after one.
    const errors = useMemo(() => {
        const found: Record<number, string> = {};
        const seen = new Map<string, number>();

        subjects.forEach((s, i) => {
            const email = s.employeeEmail.trim().toLowerCase();
            if (!s.fullName.trim()) {
                found[i] = 'Full name is required.';
            } else if (!splitFullName(s.fullName)) {
                // The API stores a surname separately, so one word is not enough.
                found[i] = 'Enter both a first and last name.';
            } else if (!email) {
                found[i] = 'Email is required.';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                found[i] = 'That email address is not valid.';
            } else if (!s.employeePhone.replace(/^\+234/, '').trim()) {
                found[i] = 'Phone number is required.';
            } else if (seen.has(email)) {
                found[i] = `Same email as person ${(seen.get(email) ?? 0) + 1}. Each person needs their own.`;
            }
            if (email && !seen.has(email)) seen.set(email, i);
        });

        return found;
    }, [subjects]);

    const isValid = Object.keys(errors).length === 0;
    const total = pricePerSubject * subjects.length;

    if (!isOpen) return null;

    const handleNext = () => {
        setTouched(true);
        if (!isValid || externalLoading) return;
        onNext(subjects.map(toSubject).filter((s): s is BatchSubject => s !== null));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Users className="h-5 w-5 text-[#FF6B35]" />
                            Who are you checking?
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Everyone here gets the <span className="font-medium text-gray-700">{planName}</span> plan,
                            paid for in one go.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                    >
                        <X className="h-4 w-4 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {/* Bulk entry by hand gets tedious past a handful of people,
                        and HR already has the list in a sheet. */}
                    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-3">
                        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#FF6B35] hover:underline">
                            <Upload className="h-4 w-4" />
                            Import from spreadsheet
                            <input
                                type="file"
                                accept={SUBJECT_IMPORT_ACCEPT}
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) void handleImport(file);
                                    // Reset so picking the same file twice re-runs.
                                    e.target.value = '';
                                }}
                            />
                        </label>
                        <span className="text-xs text-gray-400">CSV or Excel</span>
                        <button
                            type="button"
                            onClick={downloadTemplate}
                            className="ml-auto flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Download template
                        </button>
                    </div>

                    {importNote && (
                        <p className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-xs text-green-700">{importNote}</p>
                    )}
                    {importErrors.length > 0 && (
                        <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            <p className="font-medium">These rows were skipped:</p>
                            <ul className="mt-1 list-inside list-disc space-y-0.5">
                                {importErrors.slice(0, 6).map((error, i) => (
                                    <li key={i}>{error}</li>
                                ))}
                                {importErrors.length > 6 && <li>and {importErrors.length - 6} more.</li>}
                            </ul>
                        </div>
                    )}

                    <div className="flex flex-col gap-4">
                        {subjects.map((subject, index) => (
                            <div
                                key={index}
                                className={`rounded-xl border p-4 ${
                                    touched && errors[index] ? 'border-red-300 bg-red-50/40' : 'border-gray-200'
                                }`}
                            >
                                <div className="mb-3 flex items-center justify-between">
                                    <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                        Person {index + 1}
                                    </span>
                                    {subjects.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRow(index)}
                                            className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-red-600"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                            Remove
                                        </button>
                                    )}
                                </div>

                                <div className="grid gap-3 sm:grid-cols-2">
                                    {/* One name field: it is how people think of a
                                        name and how a spreadsheet stores it. Split
                                        into first/middle/last on submit. */}
                                    <input
                                        value={subject.fullName}
                                        onChange={(e) => update(index, { fullName: e.target.value })}
                                        placeholder="Full name"
                                        className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] sm:col-span-2"
                                    />
                                    <input
                                        type="email"
                                        value={subject.employeeEmail}
                                        onChange={(e) => update(index, { employeeEmail: e.target.value })}
                                        placeholder="Email address"
                                        className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35]"
                                    />
                                    {/* Same shape as the single flow: the country
                                        code is fixed and the number typed after it. */}
                                    <div className="flex items-center rounded-lg border border-gray-200 focus-within:border-[#FF6B35]">
                                        <span className="border-r border-gray-200 px-3 py-2.5 text-sm text-gray-500">
                                            +234
                                        </span>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            value={subject.employeePhone.replace(/^\+234/, '')}
                                            onChange={(e) =>
                                                update(index, {
                                                    employeePhone: `+234${e.target.value.replace(/\D/g, '')}`,
                                                })
                                            }
                                            placeholder="8012345678"
                                            className="w-full rounded-r-lg px-3 py-2.5 text-sm outline-none"
                                        />
                                    </div>
                                    <select
                                        value={subject.employeeGender}
                                        onChange={(e) => update(index, { employeeGender: e.target.value })}
                                        className={`rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] ${
                                            subject.employeeGender ? 'text-gray-900' : 'text-gray-400'
                                        }`}
                                    >
                                        <option value="">Gender (optional)</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                    </select>
                                    <select
                                        value={subject.investigationCategory}
                                        onChange={(e) => update(index, { investigationCategory: e.target.value })}
                                        className={`rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] ${
                                            subject.investigationCategory ? 'text-gray-900' : 'text-gray-400'
                                        }`}
                                    >
                                        <option value="">Category (optional)</option>
                                        <option value={InvestigationCategory.EMPLOYMENT}>Employment</option>
                                        <option value={InvestigationCategory.DOMESTIC_STAFF}>Domestic staff</option>
                                    </select>
                                </div>

                                {touched && errors[index] && (
                                    <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                        {errors[index]}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    {subjects.length < MAX_SUBJECTS ? (
                        <button
                            type="button"
                            onClick={addRow}
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 py-3 text-sm font-medium text-gray-600 transition-colors hover:border-[#FF6B35] hover:text-[#FF6B35]"
                        >
                            <Plus className="h-4 w-4" />
                            Add another person
                        </button>
                    ) : (
                        <p className="mt-4 text-center text-xs text-gray-400">
                            {MAX_SUBJECTS} people is the most you can pay for in one go. Submit these, then start
                            another batch.
                        </p>
                    )}

                    <p className="mt-4 text-xs text-gray-400">
                        Documents are uploaded per person after payment, from each investigation's page.
                    </p>
                </div>

                <div className="border-t border-gray-100 px-6 py-4">
                    {externalError && (
                        <p className="mb-3 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            {externalError}
                        </p>
                    )}
                    <div className="flex items-center justify-between gap-4">
                        <div className="text-sm">
                            <p className="text-gray-500">
                                {subjects.length} {subjects.length === 1 ? 'person' : 'people'} ×{' '}
                                {formatNaira(pricePerSubject)}
                            </p>
                            <p className="text-lg font-bold text-gray-900 tabular-nums">{formatNaira(total)}</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={onBack}
                                disabled={externalLoading}
                                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={externalLoading || (touched && !isValid)}
                                className="rounded-xl bg-[#FF6B35] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#15171C] disabled:opacity-50"
                            >
                                {externalLoading ? 'Creating…' : 'Continue to payment'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
